import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import { Dialog, DialogButton, DialogContentText } from '@/components/Dialog';
import useDataHubExport from '@/features/DataHub/modules/Export/hooks/useDataHubExport';
import useUserDetails from '@/hooks/useUserDetails';

import { ExportMetaProvider } from './ExportMetaContext';
import { ExportPreviewProvider } from './ExportPreviewContext';
import { ExportRequestProvider, useExportRequest } from './ExportRequestContext';
import { ExportSettingsProvider } from './ExportSettingsContext';

import type { ExportContextType, ExportSettingsType } from '../types/context.types';

const ExportContext = createContext<ExportContextType | undefined>(undefined);

export const useExport = () => {
  const context = useContext(ExportContext);
  if (!context) throw new Error('useExportContext must be used within ExportProvider.');
  return context;
};

const ExcelExportModalsWrapper = () => {
  const { excelExportStatus, resetExcelExportStatus } = useExportRequest();

  const isLoading = excelExportStatus === 'loading';
  const isQueued = excelExportStatus === 'queued';

  const { userEmail } = useUserDetails();

  return (
    <>
      <Dialog
        open={isLoading}
        onClose={() => {}}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%', py: '16px' }}>
          <CircularProgress size={24} />
          <DialogContentText>Your request is being processed...</DialogContentText>
        </Box>
      </Dialog>

      <Dialog
        buttons={<DialogButton onClick={resetExcelExportStatus}>OK</DialogButton>}
        open={isQueued}
        title="We're on it."
        onClose={resetExcelExportStatus}
      >
        <DialogContentText>
          You will get an email shortly to{' '}
          <Typography
            sx={{
              textDecoration: 'underline',
              display: 'inline'
            }}
          >
            {userEmail}
          </Typography>
        </DialogContentText>
      </Dialog>
    </>
  );
};

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const exportContext = useDataHubExport();
  const updateExportSetting = useCallback(
    <K extends keyof ExportSettingsType>(key: K, value: ExportSettingsType[K]) => {
      exportContext.setExportSettings(prev => ({ ...prev, [key]: value }));
    },
    [exportContext]
  );

  const settingsValue = useMemo(
    () => ({
      exportSettings: exportContext.exportSettings,
      setExportSettings: exportContext.setExportSettings,
      updateExportSetting
    }),
    [exportContext.exportSettings, exportContext.setExportSettings, updateExportSetting]
  );

  const previewValue = useMemo(
    () => ({
      previewOptionId: exportContext.previewOptionId,
      setPreviewOptionId: exportContext.setPreviewOptionId,
      previewLoading: exportContext.previewLoading,
      setPreviewLoading: exportContext.setPreviewLoading,
      previewDownloadFn: exportContext.previewDownloadFn,
      setPreviewDownloadFn: exportContext.setPreviewDownloadFn,
      previewContentToCopy: exportContext.previewContentToCopy,
      setPreviewContentToCopy: exportContext.setPreviewContentToCopy,
      isExcelPreview: exportContext.isExcelPreview
    }),
    [
      exportContext.previewOptionId,
      exportContext.setPreviewOptionId,
      exportContext.previewLoading,
      exportContext.setPreviewLoading,
      exportContext.previewDownloadFn,
      exportContext.setPreviewDownloadFn,
      exportContext.previewContentToCopy,
      exportContext.setPreviewContentToCopy,
      exportContext.isExcelPreview
    ]
  );

  const requestValue = useMemo(
    () => ({
      exportData: exportContext.exportData,
      exportDataWithParams: exportContext.exportDataWithParams,
      loading: exportContext.loading,
      exportError: exportContext.exportError,
      showSuccessScreen: exportContext.showSuccessScreen,
      setShowSuccessScreen: exportContext.setShowSuccessScreen,
      excelExportStatus: exportContext.excelExportStatus,
      setExcelExportStatus: exportContext.setExcelExportStatus,
      resetExcelExportStatus: exportContext.resetExcelExportStatus,
      handleCopyToClipBoard: exportContext.handleCopyToClipBoard,
      handleExportData: exportContext.handleExportData,
      capturePosthogEvent: exportContext.capturePosthogEvent,
      scrambleCurlApiKey: exportContext.scrambleCurlApiKey,
      scrambleApiToken: exportContext.scrambleApiToken,
      resetExportState: exportContext.resetExportState,
      requestClose: exportContext.requestClose,
      setRequestClose: exportContext.setRequestClose
    }),
    [
      exportContext.exportData,
      exportContext.exportDataWithParams,
      exportContext.loading,
      exportContext.exportError,
      exportContext.showSuccessScreen,
      exportContext.setShowSuccessScreen,
      exportContext.excelExportStatus,
      exportContext.setExcelExportStatus,
      exportContext.resetExcelExportStatus,
      exportContext.handleCopyToClipBoard,
      exportContext.handleExportData,
      exportContext.capturePosthogEvent,
      exportContext.scrambleCurlApiKey,
      exportContext.scrambleApiToken,
      exportContext.resetExportState,
      exportContext.requestClose,
      exportContext.setRequestClose
    ]
  );

  const metaValue = useMemo(
    () => ({
      exportName: exportContext.exportName,
      setExportName: exportContext.setExportName,
      uniqueSymbols: exportContext.uniqueSymbols,
      exportApiRequestString: exportContext.exportApiRequestString,
      exportCurlRequestString: exportContext.exportCurlRequestString,
      timezone: exportContext.timezone
    }),
    [
      exportContext.exportName,
      exportContext.setExportName,
      exportContext.uniqueSymbols,
      exportContext.exportApiRequestString,
      exportContext.exportCurlRequestString,
      exportContext.timezone
    ]
  );

  return (
    <ExportContext.Provider value={exportContext}>
      <ExportSettingsProvider value={settingsValue}>
        <ExportPreviewProvider value={previewValue}>
          <ExportRequestProvider value={requestValue}>
            <ExportMetaProvider value={metaValue}>
              {children}
              <ExcelExportModalsWrapper />
            </ExportMetaProvider>
          </ExportRequestProvider>
        </ExportPreviewProvider>
      </ExportSettingsProvider>
    </ExportContext.Provider>
  );
};
